import * as H from 'history';
import * as React from 'react';
import * as _ from 'underscore';

import { ConnectedAdminAnalysisVizSpaceProvider, getAdminVizPathPrefix, renderAdminAnalysisVizSpace } from './AdminAnalysisVizSpaceProvider';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';

import { BreadcrumbStateInfo } from '../../content/breadcrumbs/BreadcrumbStateInfo';
import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { Messages } from '../../../codegen/Localize';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { ServerUrls } from '../../ServerUrls';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { VizPathInitialState } from '../../reducers/VizPathReducer';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { selectVizPath } from '../../selectors/VizPathSelector';
import { updateBreadcrumbs } from '../../actions/UpdateBreadcrumbsAction';

export namespace AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater {
  export interface OwnProps extends ReactDirectiveProps {
    readonly sheetUrlId: AdminViewName;
    readonly routeLocationSearch: string;
    readonly history: H.History;
    readonly urls: SpacesSiteUrls;
    readonly isServerAnalysis: boolean;
    readonly renderSpaceProvider: (props: ContainerProps, spaceInfo: SpaceInfo) => JSX.Element;
  }

  export const renderProvider = (props: ContainerProps, spaceInfo: SpaceInfo): JSX.Element => {
    return (
      <ConnectedSpaceInfoProvider spaceInfo={spaceInfo} >
        <ConnectedAdminAnalysisVizSpaceProvider
          sheetUrlId={props.sheetUrlId}
          routeLocationSearch={props.routeLocationSearch}
          urls={props.urls}
          isServerAnalysis={props.isServerAnalysis}
          angularContext={props.angularContext}
          renderSpace={renderAdminAnalysisVizSpace} />
      </ConnectedSpaceInfoProvider>
    );
  };

  export interface DispatchProps {
    readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
  }

  export interface StateProps {
    readonly vizPath: string;
  }

  export type NonAngularProps = OwnProps & DispatchProps & StateProps;

  export type ContainerProps = OwnProps & DispatchProps & StateProps;

  export type AdminViewName = 'ActionsbyAllUsers'
    | 'ActionsbySpecificUser'
    | 'ActionsbyRecentUsers'
    | 'BackgroundTaskDelays'
    | 'BackgroundTasksforExtracts'
    | 'BridgeTasksforExtracts'
    | 'BackgroundTasksforNonExtracts'
    | 'BackgrounderDashboard'
    | 'BackgrounderDashboardSite'
    | 'DesktopLicenseUsage'
    | 'DesktopLicenseExpirations'
    | 'LoginBasedLicenseUsage'
    | 'ServerDiskSpace'
    | 'PerformanceofViews'
    | 'PerformanceofFlowRuns'
    | 'PopularViews'
    | 'StatsforLoadTimes'
    | 'StatsforSpaceUsage'
    | 'TraffictoBridgeconnectedDatasources'
    | 'TraffictoDataSources'
    | 'TraffictoViews'
    | 'StaleContentSiteAdmin'
    | 'StaleContent'
    | 'ServerOverviewSiteAdmin'
    | 'ServerOverview';

  export class Container extends React.PureComponent<ContainerProps> {

    // We can't get admin view names from the Views service, so we use hardcoded loc strings
    private static ViewNameLocKeyDict: Record<AdminViewName, () => string> = {
      ActionsbyAllUsers: Messages.actionsByAllUsersTitle,
      ActionsbySpecificUser: Messages.actionsByASpecificUserTitle,
      ActionsbyRecentUsers: Messages.actionsByRecentlySignedInUsersTitle,
      BackgroundTaskDelays: Messages.backgroundTaskDelaysTitle,
      BackgroundTasksforExtracts: Messages.backgroundTasksExtractsTitle,
      BridgeTasksforExtracts: Messages.bridgeTasksExtractsTitle,
      BackgroundTasksforNonExtracts: Messages.backgroundTasksNonExtractsTitle,
      BackgrounderDashboard: Messages.backgrounderDashboardTitle,
      BackgrounderDashboardSite: Messages.backgrounderDashboardTitle,
      DesktopLicenseUsage: Messages.desktopLicenseUsageTitle,
      DesktopLicenseExpirations: Messages.desktopLicenseExpirationsTitle,
      LoginBasedLicenseUsage: Messages.ibaLicenseUsageTitle,
      ServerDiskSpace: Messages.serverDiskSpaceTitle,
      PerformanceofViews: Messages.performanceofViewsTitle,
      PerformanceofFlowRuns: Messages.performanceofFlowRunsTitle,
      PopularViews: Messages.popularViewsTitle,
      StatsforLoadTimes: Messages.statsLoadTimesTitle,
      StatsforSpaceUsage: Messages.statsSpaceUsageTitle,
      TraffictoBridgeconnectedDatasources: Messages.trafficToBridgeDataSourcesTitle,
      TraffictoDataSources: Messages.trafficToDataSourcesTitle,
      TraffictoViews: Messages.trafficToViewsTitle,
      StaleContentSiteAdmin: Messages.staleContentTitle,
      StaleContent: Messages.staleContentTitle,
      ServerOverviewSiteAdmin: Messages.serverOverViewTitle,
      ServerOverview: Messages.serverOverViewTitle,
    };

    public constructor(props: ContainerProps) {
      super(props);
    }

    public render() {
      const spaceInfo: SpaceInfo = {
        item: SpaceItemForNonEntitySpace,
        spaceType: this.props.isServerAnalysis? 'serveranalysis' : 'siteanalysis',
        subspaceName: undefined
      };

      return (
        <>
          { this.props.renderSpaceProvider(this.props, spaceInfo) }
        </>
      );
    }

    public componentDidMount() {
      this.updateTitleAndBreadcrumbs();
    }

    public componentDidUpdate(prevProps: ContainerProps) {
      if (prevProps.sheetUrlId !== this.props.sheetUrlId ||
          prevProps.isServerAnalysis !== this.props.isServerAnalysis) {
        this.updateTitleAndBreadcrumbs();
      }

      const vizPathPrefix = getAdminVizPathPrefix(this.props.sheetUrlId, this.props.isServerAnalysis);
      if (this.props.vizPath !== VizPathInitialState &&
          this.props.vizPath !== prevProps.vizPath &&
          this.props.vizPath !== (vizPathPrefix + this.props.sheetUrlId)) {
          // vizPath changed via user clicking on viz tab (that's why the url does not match the new vizPath-- see tbViz),
          // so update the url
          const newSheetUrlId = this.props.vizPath.split('/')[1];
          this.props.history.replace(newSheetUrlId);
      }
    }

    private updateTitleAndBreadcrumbs = () => {
      const workbookName = this.props.isServerAnalysis ? Messages.serverStatus() : Messages.siteStatus();
      BrowserTitleService.setVizTitle(this.getViewName(), workbookName);
      this.props.updateBreadcrumbs(this.buildBreadcrumbs());
    }

    private getViewName() {
      return Container.ViewNameLocKeyDict[this.props.sheetUrlId as AdminViewName]();
    }

    private buildBreadcrumbs(): BreadcrumbStateInfo[] {
      return [
        this.props.isServerAnalysis ? this.serverStatusBreadcrumb() : this.siteStatusBreadcrumb(),
        { text: this.getViewName() }
      ];
    }

    private serverStatusBreadcrumb(): BreadcrumbStateInfo {
      return {
        id: ServerUrls.getInstance().statusUrl(),
        text: Messages.serverStatus(),
        href: ServerUrls.getInstance().statusUrl(),
        testId: 'server-status-link'
      };
    }

    private siteStatusBreadcrumb(): BreadcrumbStateInfo {
      return {
        id: this.props.urls.statusUrl(),
        text: Messages.siteStatus(),
        href: this.props.urls.statusUrl(),
        testId: 'site-status-link'
      };
    }
  }

  const mapStateToProps: MapStateToProps<StateProps> = (appState) => ({
    vizPath: selectVizPath(appState)
  });

  const mapDispatchToProps: MapDispatchToProps<DispatchProps> = (dispatch) => bindActionCreators({
    updateBreadcrumbs
  }, dispatch);

  export const ConnectedContainer: React.ComponentClass<OwnProps>
    = connect(mapStateToProps, mapDispatchToProps)(Container);

  export function ConnectedUpdater(props: OwnProps) {
    return <ConnectedContainer {...props} />;
  }
}
